// src/shared/components/layout/NavBar.jsx
import React, { useCallback, useState } from 'react';
import {
  AppBar, Toolbar, IconButton, Box, Skeleton, Menu, MenuItem,
  Typography, ListItemIcon, Divider, Tooltip
} from '@mui/material';
import { Menu as MenuIcon, Logout, Person } from '@mui/icons-material';
import { useTheme, styled, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import useResponsive from '@shared/hooks/useResponsive';
import UserAvatar from './Avatar';
import { useAuth } from '@app/providers/AuthProvider';
import AsyncCityAutocomplete from '@features/bookings/components/AsyncCityAutoComplete';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
  boxShadow: 'none',
  backgroundImage: 'none',
  color: theme.palette.text.primary,
  transition: theme.transitions.create(['width', 'margin'], {
    duration: theme.transitions.duration.shorter,
  }),
}));

const NavBar = ({ handleDrawerToggle }) => {
  const { user, signOut, loading, error } = useAuth();
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [sessionToken] = useState(() => Math.random().toString(36).slice(2));

  const roles = user?.roles ?? [];
  const hasRole = (name) => roles.some((r) => r?.name === name);
  const isMsiStaff =
    hasRole('MSI Staff') || hasRole('Admin') || hasRole('Super Admin');
  const showSearch = user && !isMsiStaff;

  const handleMenu = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogoutClick = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      signOut();
      handleClose();
    }
  };

  const handleProfileClick = () => {
    handleClose();
    navigate('/profile');
  };

  const handleDestinationSelect = useCallback(
    (selected) => {
      if (!selected) return;
      navigate('/booking', {
        state: { prefill: { city: selected }, autoSearch: false },
      });
    },
    [navigate]
  );

  return (
    <StyledAppBar
      position="sticky"
      sx={{
        width: '100%',
        transition: '0.3s',
      }}
    >
      <Toolbar sx={{ minHeight: 64, px: { xs: 1.5, md: 3 }, gap: { xs: 1.25, md: 2 } }}>
        {isMobile && (
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
        )}

       

        {showSearch ? (
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              mx: { xs: 0, md: 2 },
              mr: { xs: 1.25, md: 2 },
              display: 'flex',
            }}
          >
            <Box
              sx={{
                width: { xs: '100%', sm: 300 },
                maxWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '50px',
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(0,0,0,0.05)',
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': {
                    border: '1.5px solid',
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root': { display: 'none' },
                '& .MuiInputBase-input': { py: '7px' },
              }}
            >
              <AsyncCityAutocomplete
                label=""
                value={null}
                onChange={handleDestinationSelect}
                sessionToken={sessionToken}
                country="us"
                size="small"
                minWidth={300}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1 }} />
        )}

        {user ? (
          <>
            <IconButton
              size="large"
              edge="end"
              aria-controls={open ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleMenu}
              color="inherit"
              sx={{ ml: { xs: 0.5, md: 1 }, flexShrink: 0 }}
            >
              <UserAvatar name={`${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || 'User'} />
            </IconButton>
            <Menu
              id="account-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              PaperProps={{
                elevation: 4,
                sx: {
                  mt: '45px',
                  minWidth: 200,
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  '& .MuiAvatar-root': { width: 32, height: 32, ml: -0.5, mr: 1 },
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 20,
                    right: 15,
                    width: 10,
                    height: 10,
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
              <MenuItem onClick={handleProfileClick}>
                <ListItemIcon><Person fontSize="small" /></ListItemIcon>
                Profile
              </MenuItem>
              <Divider sx={{ my: 1 }} />
              <MenuItem onClick={handleLogoutClick} disabled={loading}>
                <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Skeleton variant="circular" width={40} height={40} sx={{ mt: 1, mr: 2 }} />
        )}
      </Toolbar>

      {error && (
        <Box sx={{ p: 1, backgroundColor: 'error.main', color: 'white' }}>
          <Typography variant="body2">{error}</Typography>
        </Box>
      )}
    </StyledAppBar>
  );
};

export default NavBar;